import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useAuth } from "../../../../firebase/AuthContext";
import { FAQ_API } from "../api/config";
import { batchApproveReview, batchRejectReview } from "../api/faq";

const PAGE_SIZE = 20;

const SOURCE_LABELS = {
  web_search: "Web Search",
  website_intro: "Info from TG website",
};

const SOURCE_CARD_STYLES = {
  web_search: "bg-orange-50 border-orange-200 text-orange-700",
  website_intro: "bg-sky-50 border-sky-200 text-sky-700",
};

const formatAskedBy = (askedBy) => {
  if (!askedBy) return "Unknown";
  return askedBy === "anonymous" ? "Guest" : askedBy;
};

const formatCreatedAt = (createdAt) =>
  createdAt ? new Date(createdAt).toLocaleString() : "—";

const Spinner = () => (
  <div className="flex justify-center py-8">
    <svg
      className="animate-spin h-8 w-8 text-orange-500"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  </div>
);

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "all", label: "All" },
];

const STATUS_STAT_META = [
  {
    key: "pending",
    label: "Pending",
    border: "border-l-orange-500",
    chip: "bg-orange-500",
    icon: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
  },
  {
    key: "approved",
    label: "Approved",
    border: "border-l-emerald-500",
    chip: "bg-emerald-500",
    icon: "M5 13l4 4L19 7",
  },
  {
    key: "rejected",
    label: "Rejected",
    border: "border-l-red-500",
    chip: "bg-red-500",
    icon: "M6 18L18 6M6 6l12 12",
  },
];

const SOURCE_STAT_META = {
  web_search: {
    border: "border-l-orange-500",
    chip: "bg-orange-500",
    icon: "M21 21l-4.3-4.3m1.8-5.4a7.2 7.2 0 11-14.4 0 7.2 7.2 0 0114.4 0z",
  },
  website_intro: {
    border: "border-l-sky-500",
    chip: "bg-sky-500",
    icon: "M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  },
};
const DEFAULT_SOURCE_STAT_META = {
  border: "border-l-gray-400",
  chip: "bg-gray-400",
  icon: "M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
};

function ReviewTab() {
  const { currentUser } = useAuth();
  const [items, setItems] = useState([]);
  const [sourceCounts, setSourceCounts] = useState({});
  const [status, setStatus] = useState("pending");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [statusCounts, setStatusCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [actioningId, setActioningId] = useState(null);
  const [sourceFilter, setSourceFilter] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [batchSubmitting, setBatchSubmitting] = useState(false);

  const fetchQueue = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    setError("");
    try {
      const token = await currentUser.getIdToken();
      const headers = { authorization: `Bearer ${token}` };
      // /review/stats groups by source, not by status — there's no confirmed way
      // to get a status breakdown from it, so we derive counts by calling
      // /review/queue once per status with page_size=1 and reading `total`.
      const sourceParam = sourceFilter || undefined;
      const [queueRes, statsRes, pendingRes, approvedRes, rejectedRes] =
        await Promise.all([
          axios.get(`${FAQ_API}/review/queue`, {
            params: { status, page, page_size: pageSize, source: sourceParam },
            headers,
          }),
          axios.get(`${FAQ_API}/review/stats`, {
            params: { status: "all", source: sourceParam },
            headers,
          }),
          axios.get(`${FAQ_API}/review/queue`, {
            params: {
              status: "pending",
              page: 1,
              page_size: 1,
              source: sourceParam,
            },
            headers,
          }),
          axios.get(`${FAQ_API}/review/queue`, {
            params: {
              status: "approved",
              page: 1,
              page_size: 1,
              source: sourceParam,
            },
            headers,
          }),
          axios.get(`${FAQ_API}/review/queue`, {
            params: {
              status: "rejected",
              page: 1,
              page_size: 1,
              source: sourceParam,
            },
            headers,
          }),
        ]);
      setItems(queueRes.data.items || []);
      setTotal(queueRes.data.total ?? 0);
      setPage(queueRes.data.page ?? page);
      setPageSize(queueRes.data.page_size ?? pageSize);
      setSourceCounts(statsRes.data || {});
      setStatusCounts({
        pending: pendingRes.data.total ?? 0,
        approved: approvedRes.data.total ?? 0,
        rejected: rejectedRes.data.total ?? 0,
      });
      setSelectedIds(new Set());
    } catch (err) {
      console.error("[ReviewTab] Failed to fetch review queue:", err);
      setError("Failed to load review queue. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [currentUser, status, page, pageSize, sourceFilter]);

  const changeStatus = (nextStatus) => {
    setStatus(nextStatus);
    setPage(1);
  };

  const changeSourceFilter = (nextSource) => {
    setSourceFilter(nextSource);
    setPage(1);
  };

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const toggleExpand = (logId) => {
    setExpandedId((current) => (current === logId ? null : logId));
  };

  const handleDecision = async (logId, decision) => {
    setActioningId(logId);
    try {
      const token = await currentUser.getIdToken();
      await axios.put(
        `${FAQ_API}/review/${logId}/${decision}`,
        {},
        { headers: { authorization: `Bearer ${token}` } },
      );
      // Re-fetch instead of locally filtering the item out — under a
      // status=approved/rejected/all filter, deciding an item changes
      // whether it still belongs in the current view, so a local splice
      // would be wrong for anything other than the pending filter.
      await fetchQueue();
    } catch (err) {
      console.error(`[ReviewTab] Failed to ${decision} log ${logId}:`, err);
      setError(`Failed to ${decision} this entry. Please try again.`);
    } finally {
      setActioningId(null);
    }
  };

  const toggleSelected = (logId) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(logId)) {
        next.delete(logId);
      } else {
        next.add(logId);
      }
      return next;
    });
  };

  const allSelected = items.length > 0 && selectedIds.size === items.length;
  const toggleSelectAll = () => {
    setSelectedIds(
      allSelected ? new Set() : new Set(items.map((i) => i.log_id)),
    );
  };

  const handleBatchApprove = async () => {
    setBatchSubmitting(true);
    try {
      const token = await currentUser.getIdToken();
      const result = await batchApproveReview(token, Array.from(selectedIds));
      await fetchQueue();
      if (result?.failed?.length > 0) {
        alert(
          `${result.approved?.length ?? 0} approved, ${result.failed.length} failed.`,
        );
      }
    } catch (err) {
      console.error("[ReviewTab] Failed to batch approve:", err);
      setError("Failed to approve the selected entries.");
    } finally {
      setBatchSubmitting(false);
    }
  };

  const handleBatchReject = async () => {
    if (!window.confirm(`Reject ${selectedIds.size} selected entries?`))
      return;
    setBatchSubmitting(true);
    try {
      const token = await currentUser.getIdToken();
      await batchRejectReview(token, Array.from(selectedIds));
      await fetchQueue();
    } catch (err) {
      console.error("[ReviewTab] Failed to batch reject:", err);
      setError("Failed to reject the selected entries.");
    } finally {
      setBatchSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {/* Stat cards — status breakdown always shows the full picture
          regardless of the current status filter. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 border-l-4 border-l-gray-300 dark:border-l-gray-600 p-4 flex items-center justify-between shadow-sm">
          <div>
            <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
              Total
            </p>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 tabular-nums">
              {(statusCounts.pending ?? 0) +
                (statusCounts.approved ?? 0) +
                (statusCounts.rejected ?? 0)}
            </p>
          </div>
          <div className="w-10 h-10 shrink-0 rounded-lg flex items-center justify-center text-white bg-gray-400">
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
          </div>
        </div>
        {STATUS_STAT_META.map(({ key, label, border, chip, icon }) => (
          <div
            key={key}
            className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 border-l-4 ${border} p-4 flex items-center justify-between shadow-sm`}
          >
            <div>
              <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                {label}
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 tabular-nums">
                {statusCounts[key] ?? 0}
              </p>
            </div>
            <div
              className={`w-10 h-10 shrink-0 rounded-lg flex items-center justify-center text-white ${chip}`}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d={icon}
                />
              </svg>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar: status filter (drives the queue below) + source breakdown
          as secondary context. */}
      <div className="flex items-center justify-between flex-wrap gap-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm px-4 py-3">
        <div className="flex items-center gap-2">
          <svg
            className="w-4 h-4 text-gray-400 dark:text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
            />
          </svg>
          <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
            Status
          </span>
          <div className="relative">
            <select
              value={status}
              onChange={(e) => changeStatus(e.target.value)}
              className="appearance-none pl-3 pr-8 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:border-orange-400 dark:hover:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400 cursor-pointer transition-colors"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <svg
              className="w-4 h-4 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
          <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
            Source
          </span>
          <div className="relative">
            <select
              value={sourceFilter}
              onChange={(e) => changeSourceFilter(e.target.value)}
              className="appearance-none pl-3 pr-8 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:border-orange-400 dark:hover:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400 cursor-pointer transition-colors"
            >
              <option value="">All sources</option>
              {Object.entries(SOURCE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <svg
              className="w-4 h-4 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>

        {Object.keys(sourceCounts).length > 0 && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
            <span className="font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
              By source
            </span>
            {Object.entries(sourceCounts).map(([source, count]) => {
              const meta = SOURCE_STAT_META[source] ?? DEFAULT_SOURCE_STAT_META;
              return (
                <span key={source} className="inline-flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full ${meta.chip}`} />
                  {SOURCE_LABELS[source] ?? source}
                  <span className="font-semibold text-gray-700 dark:text-gray-200 tabular-nums">
                    {count}
                  </span>
                </span>
              );
            })}
          </div>
        )}
      </div>

      {items.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm px-4 py-3">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={toggleSelectAll}
          />
          <span className="text-sm text-gray-600 dark:text-gray-300">
            {selectedIds.size > 0
              ? `${selectedIds.size} selected`
              : "Select entries to batch approve or reject"}
          </span>
          <button
            onClick={handleBatchApprove}
            disabled={batchSubmitting || selectedIds.size === 0}
            className="px-3 py-1.5 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg disabled:opacity-50 transition-colors"
          >
            Batch Approve
          </button>
          <button
            onClick={handleBatchReject}
            disabled={batchSubmitting || selectedIds.size === 0}
            className="px-3 py-1.5 text-sm font-medium text-red-600 bg-white dark:bg-gray-800 border border-red-200 dark:border-red-900/40 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg disabled:opacity-50 transition-colors"
          >
            Batch Reject
          </button>
        </div>
      )}

      {loading ? (
        <Spinner />
      ) : items.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-8 text-center">
          <p className="text-gray-500 dark:text-gray-400">
            No entries pending review.
          </p>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-3">
            {items.map((item) => {
              const isExpanded = expandedId === item.log_id;
              return (
                <div
                  key={item.log_id}
                  className="bg-orange-50/40 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-900/40 rounded-xl overflow-hidden"
                >
                  <div
                    onClick={() => toggleExpand(item.log_id)}
                    className="p-4 flex items-center justify-between gap-3 cursor-pointer hover:bg-orange-50 dark:hover:bg-orange-900/20"
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(item.log_id)}
                      onChange={() => toggleSelected(item.log_id)}
                      onClick={(e) => e.stopPropagation()}
                      className="shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-slate-900 dark:text-gray-100 truncate">
                        <strong>Q: </strong>
                        {item.question}
                      </p>
                      <p className="text-xs text-slate-400 dark:text-gray-500 mt-0.5 flex items-center gap-1.5">
                        <span
                          className={`px-1.5 py-0.5 rounded text-[11px] font-medium border shrink-0 ${
                            SOURCE_CARD_STYLES[item.source] ??
                            "bg-slate-100 border-slate-300 text-slate-500"
                          }`}
                        >
                          {SOURCE_LABELS[item.source] ?? item.source}
                        </span>
                        {item.status && item.status !== "pending" && (
                          <span
                            className={`px-1.5 py-0.5 rounded text-[11px] font-medium shrink-0 ${
                              item.status === "approved"
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-red-100 text-red-700"
                            }`}
                          >
                            {item.status}
                          </span>
                        )}
                        <span>
                          {formatAskedBy(item.asked_by)} ·{" "}
                          {formatCreatedAt(item.created_at)}
                        </span>
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {(!item.status || item.status === "pending") && (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDecision(item.log_id, "approve");
                            }}
                            disabled={actioningId === item.log_id}
                            className="px-3 py-1.5 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg disabled:opacity-50 transition-colors"
                          >
                            Approve
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDecision(item.log_id, "reject");
                            }}
                            disabled={actioningId === item.log_id}
                            className="px-3 py-1.5 text-sm font-medium text-red-600 bg-white dark:bg-gray-800 border border-red-200 dark:border-red-900/40 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg disabled:opacity-50 transition-colors"
                          >
                            Reject
                          </button>
                        </>
                      )}
                      <span className="text-slate-400 dark:text-gray-500 text-xs">
                        {isExpanded ? "▲" : "▼"}
                      </span>
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="px-4 pb-4 flex flex-col gap-2">
                      {item.rewritten_question && (
                        <p className="text-xs text-slate-400 dark:text-gray-500">
                          <strong>Rewritten as: </strong>
                          {item.rewritten_question}
                        </p>
                      )}
                      <div className="text-sm text-slate-700 dark:text-gray-300 break-words [&_table]:border-collapse [&_table]:my-2 [&_th]:border [&_th]:border-slate-300 dark:[&_th]:border-gray-600 [&_th]:bg-slate-100 dark:[&_th]:bg-gray-700 [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_td]:border [&_td]:border-slate-300 dark:[&_td]:border-gray-600 [&_td]:px-2 [&_td]:py-1">
                        <strong>A: </strong>
                        {/* singleTilde off: a spec range like `-25~+150° C` is not strikethrough. */}
                        <ReactMarkdown remarkPlugins={[[remarkGfm, { singleTilde: false }]]}>
                          {item.answer}
                        </ReactMarkdown>
                      </div>
                      {item.sources && item.sources.length > 0 && (
                        <div className="mt-1 pt-2 border-t border-orange-200/60 dark:border-orange-900/40">
                          <p className="text-xs font-medium text-slate-500 dark:text-gray-400 mb-1">
                            Sources
                          </p>
                          <ul className="flex flex-col gap-1">
                            {item.sources.map((source, i) => (
                              <li key={i} className="flex gap-1.5">
                                <span className="text-xs text-slate-400 dark:text-gray-500 shrink-0">
                                  [{i + 1}]
                                </span>
                                <a
                                  href={source.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-sm text-orange-600 dark:text-orange-400 hover:underline break-all"
                                >
                                  {source.title || source.domain}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between flex-wrap gap-3 bg-white dark:bg-gray-800 rounded-xl px-6 py-4 border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="flex items-center gap-3">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Page {page} of {totalPages} ({total} entries)
                </p>
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                  <span>Rows per page</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                    className="px-2 py-1 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 focus:outline-none focus:ring-1 focus:ring-orange-400"
                  >
                    {[10, 20, 50].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page <= 1 || loading}
                  className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 hover:text-orange-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>

                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                  // Show pages around the current page
                  let p;
                  if (totalPages <= 7) {
                    p = i + 1;
                  } else if (page <= 4) {
                    p = i + 1;
                  } else if (page >= totalPages - 3) {
                    p = totalPages - 6 + i;
                  } else {
                    p = page - 3 + i;
                  }
                  return (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      disabled={loading}
                      className={`w-8 h-8 rounded-lg text-sm font-medium transition-all ${
                        p === page
                          ? "bg-orange-500 text-white shadow-sm"
                          : "text-gray-600 dark:text-gray-300 hover:bg-orange-50 dark:hover:bg-orange-900/20 hover:text-orange-500"
                      }`}
                    >
                      {p}
                    </button>
                  );
                })}

                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= totalPages || loading}
                  className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 hover:text-orange-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ReviewTab;
